import { FormEvent, useEffect, useState } from "react";
import { Bell, BellRing, Database, HardDrive, Pencil, ScrollText, Send, ShieldCheck, UserCog, Users } from "lucide-react";
import { api } from "../api";
import type { ChecklistItem, User, VehicleType } from "../types";

type AdminChecklist = { type: VehicleType; template_id: string; items: ChecklistItem[] };
type NotificationPreferences = {
  notify_all: boolean;
  notify_ressalva: boolean;
  notify_nao_apto: boolean;
  notify_grave_critical: boolean;
};

type PushConfig = {
  configured: boolean;
  public_key: string | null;
  subscriptions: number;
  preferences: NotificationPreferences;
};

type SystemHealth = {
  ok: boolean;
  database: string;
  r2_configured: boolean;
  photo_storage: string;
  inspections: number;
  photos_total: number;
  photos_r2: number;
  photos_legacy_d1: number;
  r2_bytes_registered: number;
  d1_photo_bytes_estimated?: number;
  checked_at: string;
};

const severityLabel: Record<string, string> = {
  leve: "Leve",
  moderada: "Moderada",
  grave: "Grave",
  gravissima: "Gravíssima"
};

type AuditEntry = {
  id: string;
  action: string;
  entity: string;
  entity_id: string;
  details: string | null;
  created_at: string;
  user_name: string | null;
  user_email: string | null;
};

function formatBrt(value: string) {
  const normalized = value.includes("T") ? value : value.replace(" ", "T") + "Z";
  const d = new Date(normalized);
  return Number.isNaN(d.getTime())
    ? value
    : new Intl.DateTimeFormat("pt-BR", {
        timeZone: "America/Fortaleza",
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false
      }).format(d) + " BRT";
}

export function AdminPage() {
  const [users, setUsers] = useState<User[]>([]);
  const [checklists, setChecklists] = useState<AdminChecklist[]>([]);
  const [audit, setAudit] = useState<AuditEntry[]>([]);
  const [health, setHealth] = useState<SystemHealth | null>(null);
  const [pushConfig, setPushConfig] = useState<PushConfig | null>(null);
  const [pushPrefs, setPushPrefs] = useState<NotificationPreferences>({
    notify_all: true,
    notify_ressalva: true,
    notify_nao_apto: true,
    notify_grave_critical: true
  });
  const [pushBusy, setPushBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [userForm, setUserForm] = useState({
    name: "",
    access_mode: "email_password",
    email: "",
    password: "",
    login_username: "",
    pin: "",
    role: "inspetor"
  });
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [editForm, setEditForm] = useState({
    name: "",
    email: "",
    password: "",
    login_username: "",
    pin: "",
    role: "inspetor"
  });
  const [itemForm, setItemForm] = useState({ template_id: "", section: "Geral", question: "", severity: "moderada" });
  const [editingItem, setEditingItem] = useState<ChecklistItem | null>(null);
  const [editItemForm, setEditItemForm] = useState({ section: "", question: "", severity: "moderada" });

  async function load() {
    const [u, c, a, h, p] = await Promise.all([
      api<{ users: User[] }>("admin/users"),
      api<{ checklists: AdminChecklist[] }>("admin/checklists"),
      api<{ audit: AuditEntry[] }>("admin/audit"),
      api<SystemHealth>("admin/system-health").catch(() => null),
      api<PushConfig>("push/config").catch(() => null)
    ]);
    setUsers(u.users);
    setChecklists(c.checklists);
    setAudit(a.audit);
    setHealth(h);
    setPushConfig(p);
    if (p?.preferences) setPushPrefs(p.preferences);
    if (!itemForm.template_id && c.checklists[0]) {
      setItemForm((f) => ({ ...f, template_id: c.checklists[0].template_id }));
    }
  }

  useEffect(() => { load(); }, []);

  function urlBase64ToUint8Array(value: string) {
    const padding = "=".repeat((4 - value.length % 4) % 4);
    const base64 = (value + padding).replace(/-/g, "+").replace(/_/g, "/");
    const raw = atob(base64);
    return Uint8Array.from(raw, (char) => char.charCodeAt(0));
  }

  async function enablePushNotifications() {
    setMessage("");
    if (!pushConfig?.configured || !pushConfig.public_key) {
      setMessage("A estrutura está pronta, mas falta concluir a configuração das chaves de notificação no Cloudflare.");
      return;
    }
    if (!("serviceWorker" in navigator) || !("PushManager" in window) || !("Notification" in window)) {
      setMessage("Este navegador não oferece suporte a notificações push.");
      return;
    }

    setPushBusy(true);
    try {
      const permission = await Notification.requestPermission();
      if (permission !== "granted") {
        setMessage("A permissão de notificações não foi concedida neste dispositivo.");
        return;
      }

      const registration = await navigator.serviceWorker.ready;
      let subscription = await registration.pushManager.getSubscription();
      if (!subscription) {
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(pushConfig.public_key)
        });
      }

      await api("push/subscribe", {
        method: "POST",
        body: JSON.stringify(subscription.toJSON())
      });

      setMessage("Notificações ativadas neste dispositivo.");
      await load();
    } catch (err) {
      setMessage(err instanceof Error ? err.message : "Não foi possível ativar as notificações.");
    } finally {
      setPushBusy(false);
    }
  }

  async function disablePushNotifications() {
    setMessage("");
    setPushBusy(true);
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      if (subscription) {
        await api("push/unsubscribe", {
          method: "POST",
          body: JSON.stringify({ endpoint: subscription.endpoint })
        });
        await subscription.unsubscribe();
      }
      setMessage("Notificações desativadas neste dispositivo.");
      await load();
    } catch (err) {
      setMessage(err instanceof Error ? err.message : "Não foi possível desativar as notificações.");
    } finally {
      setPushBusy(false);
    }
  }

  async function savePushPreferences() {
    setMessage("");
    setPushBusy(true);
    try {
      await api("push/preferences", {
        method: "PATCH",
        body: JSON.stringify(pushPrefs)
      });
      setMessage("Preferências de notificação salvas.");
      await load();
    } catch (err) {
      setMessage(err instanceof Error ? err.message : "Não foi possível salvar as preferências.");
    } finally {
      setPushBusy(false);
    }
  }

  async function testPushNotification() {
    setMessage("");
    setPushBusy(true);
    try {
      const result = await api<{ delivered: number; failed: number }>("push/test", { method: "POST" });
      setMessage(
        result.delivered > 0
          ? "Notificação de teste enviada para este administrador."
          : "Nenhuma notificação foi entregue. Verifique se este dispositivo está ativado."
      );
    } catch (err) {
      setMessage(err instanceof Error ? err.message : "Não foi possível enviar a notificação de teste.");
    } finally {
      setPushBusy(false);
    }
  }

  async function createUser(event: FormEvent) {
    event.preventDefault();
    setMessage("");
    try {
      await api("admin/users", { method: "POST", body: JSON.stringify(userForm) });
      setUserForm({
        name: "",
        access_mode: "email_password",
        email: "",
        password: "",
        login_username: "",
        pin: "",
        role: "inspetor"
      });
      setMessage("Usuário criado com sucesso.");
      await load();
    } catch (err) {
      setMessage(err instanceof Error ? err.message : "Não foi possível criar o usuário.");
    }
  }

  function openEditUser(user: User) {
    setEditingUser(user);
    setEditForm({
      name: user.name,
      email: user.email || "",
      password: "",
      login_username: user.login_username || "",
      pin: "",
      role: user.role
    });
  }

  async function saveUser(event: FormEvent) {
    event.preventDefault();
    if (!editingUser) return;
    setMessage("");
    try {
      await api("admin/users/" + editingUser.id, {
        method: "PATCH",
        body: JSON.stringify(editForm)
      });
      setEditingUser(null);
      setMessage("Usuário atualizado.");
      await load();
    } catch (err) {
      setMessage(err instanceof Error ? err.message : "Não foi possível atualizar o usuário.");
    }
  }

  async function toggleUser(user: User) {
    setMessage("");
    try {
      await api("admin/users/" + user.id, {
        method: "PATCH",
        body: JSON.stringify({ active: user.active ? 0 : 1 })
      });
      await load();
    } catch (err) {
      setMessage(err instanceof Error ? err.message : "Não foi possível alterar o usuário.");
    }
  }

  async function addItem(event: FormEvent) {
    event.preventDefault();
    await api("admin/checklists/items", { method: "POST", body: JSON.stringify(itemForm) });
    setItemForm({ ...itemForm, question: "" });
    await load();
  }

  function openEditItem(item: ChecklistItem) {
    setEditingItem(item);
    setEditItemForm({
      section: item.section,
      question: item.question,
      severity: item.severity
    });
  }

  async function saveItem(event: FormEvent) {
    event.preventDefault();
    if (!editingItem) return;
    setMessage("");

    try {
      await api("admin/checklists/items/" + editingItem.id, {
        method: "PATCH",
        body: JSON.stringify(editItemForm)
      });
      setEditingItem(null);
      setMessage("Pergunta do checklist atualizada com sucesso.");
      await load();
    } catch (err) {
      setMessage(err instanceof Error ? err.message : "Não foi possível atualizar a pergunta.");
    }
  }

  async function toggleItem(item: ChecklistItem) {
    await api("admin/checklists/items/" + item.id, {
      method: "PATCH",
      body: JSON.stringify({ active: item.active ? 0 : 1 })
    });
    await load();
  }

  const admins = users.filter((u) => u.role === "admin" && u.active).length;
  const inspectors = users.filter((u) => u.role === "inspetor" && u.active).length;

  return (
    <section>
      <div className="page-title">
        <div>
          <h1>Administração</h1>
          <p>Usuários, perfis de acesso, checklists e trilha de auditoria.</p>
        </div>
      </div>

      {message && <div className="alert">{message}</div>}

      <div className="admin-kpis">
        <div><ShieldCheck size={22} /><strong>{admins}</strong><span>Administradores ativos</span></div>
        <div><Users size={22} /><strong>{inspectors}</strong><span>Inspetores ativos</span></div>
        <div><ScrollText size={22} /><strong>{audit.length}</strong><span>Eventos de auditoria carregados</span></div>
      </div>

      <div className="panel notification-settings-panel">
        <div className="notification-settings-heading">
          <div className="panel-title-icon">
            <BellRing size={20} />
            <div>
              <span className="section-kicker">NOTIFICAÇÕES</span>
              <h2>Alertas de inspeções</h2>
            </div>
          </div>
          <span className={"storage-status " + (pushConfig?.configured ? "ready" : "warning")}>
            {pushConfig?.configured ? "Push configurado" : "Configuração pendente"}
          </span>
        </div>

        <p className="muted notification-help">
          Cada administrador precisa ativar as notificações no próprio celular ou computador. Ao tocar no alerta, o MM Fleet abre diretamente o relatório da inspeção.
        </p>

        <div className="notification-device-row">
          <div>
            <Bell size={19} />
            <div>
              <strong>Este administrador</strong>
              <span>{pushConfig?.subscriptions || 0} dispositivo(s) vinculado(s)</span>
            </div>
          </div>
          <div className="actions">
            <button
              type="button"
              className="primary"
              disabled={pushBusy || !pushConfig?.configured}
              onClick={enablePushNotifications}
            >
              <BellRing size={16} /> Ativar neste dispositivo
            </button>
            <button
              type="button"
              className="secondary"
              disabled={pushBusy}
              onClick={disablePushNotifications}
            >
              Desativar
            </button>
            <button
              type="button"
              className="secondary"
              disabled={pushBusy || !pushConfig?.configured || !pushConfig?.subscriptions}
              onClick={testPushNotification}
            >
              <Send size={16} /> Testar
            </button>
          </div>
        </div>

        <div className="notification-options">
          <label>
            <input
              type="checkbox"
              checked={pushPrefs.notify_all}
              onChange={(e) => setPushPrefs({ ...pushPrefs, notify_all: e.target.checked })}
            />
            <span><strong>Receber todas as inspeções finalizadas</strong><small>Envia um alerta para cada inspeção concluída.</small></span>
          </label>
          <label>
            <input
              type="checkbox"
              checked={pushPrefs.notify_ressalva}
              onChange={(e) => setPushPrefs({ ...pushPrefs, notify_ressalva: e.target.checked })}
            />
            <span><strong>Alertar inspeções com ressalva</strong><small>Usado quando “todas” estiver desativado.</small></span>
          </label>
          <label>
            <input
              type="checkbox"
              checked={pushPrefs.notify_nao_apto}
              onChange={(e) => setPushPrefs({ ...pushPrefs, notify_nao_apto: e.target.checked })}
            />
            <span><strong>Alertar inspeções não aptas</strong><small>Usado quando “todas” estiver desativado.</small></span>
          </label>
          <label>
            <input
              type="checkbox"
              checked={pushPrefs.notify_grave_critical}
              onChange={(e) => setPushPrefs({ ...pushPrefs, notify_grave_critical: e.target.checked })}
            />
            <span><strong>Alerta prioritário para NC Grave/Gravíssima</strong><small>O título da notificação recebe destaque prioritário.</small></span>
          </label>
        </div>

        <button type="button" className="secondary notification-save" disabled={pushBusy} onClick={savePushPreferences}>
          Salvar preferências
        </button>

        {!pushConfig?.configured && (
          <p className="production-health-warning">
            A interface já está pronta. Falta somente cadastrar as chaves VAPID no Cloudflare para liberar o botão de ativação.
          </p>
        )}
      </div>

      {health && (
        <div className="panel production-health ready">
          <div className="production-health-title">
            <div>
              <span className="section-kicker">STATUS DE PRODUÇÃO</span>
              <h2>Armazenamento e integridade</h2>
            </div>
            <span className="storage-status ready">D1 ativo</span>
          </div>

          <div className="production-health-grid">
            <div>
              <Database size={19} />
              <strong>{health.inspections}</strong>
              <span>Inspeções</span>
            </div>
            <div>
              <HardDrive size={19} />
              <strong>{health.photos_total}</strong>
              <span>Fotos registradas</span>
            </div>
            <div>
              <Database size={19} />
              <strong>{health.photos_legacy_d1}</strong>
              <span>Fotos no D1</span>
            </div>
            <div>
              <HardDrive size={19} />
              <strong>{((health.d1_photo_bytes_estimated || 0) / 1024 / 1024).toFixed(1)} MB</strong>
              <span>Volume aprox. das fotos</span>
            </div>
          </div>

          <p className="production-health-note">
            O armazenamento atual das fotos é o D1. O tamanho total oficial do banco deve ser acompanhado no painel da Cloudflare em D1 → Metrics → Storage.
          </p>
        </div>
      )}

      <div className="two-columns">
        <form className="panel admin-user-form" onSubmit={createUser}>
          <div className="panel-title-icon"><UserCog size={20} /><h2>Novo usuário</h2></div>
          <p className="muted">
            Contas atuais com e-mail continuam normalmente. Para colaboradores sem e-mail corporativo, use Usuário + PIN.
          </p>

          <label>Nome
            <input required value={userForm.name} onChange={(e) => setUserForm({ ...userForm, name: e.target.value })} />
          </label>

          <label>Forma de acesso
            <select
              value={userForm.access_mode}
              onChange={(e) => setUserForm({
                ...userForm,
                access_mode: e.target.value,
                role: e.target.value === "username_pin" ? "inspetor" : userForm.role
              })}
            >
              <option value="email_password">E-mail + senha</option>
              <option value="username_pin">Usuário + PIN (sem e-mail)</option>
            </select>
          </label>

          {userForm.access_mode === "username_pin" ? (
            <>
              <label>Usuário de acesso
                <input
                  required
                  minLength={3}
                  maxLength={30}
                  autoCapitalize="none"
                  placeholder="Ex.: cassio"
                  value={userForm.login_username}
                  onChange={(e) => setUserForm({ ...userForm, login_username: e.target.value.toLowerCase() })}
                />
                <small>Letras minúsculas, números, ponto, hífen ou underline.</small>
              </label>
              <label>PIN de 6 dígitos
                <input
                  required
                  type="password"
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  minLength={6}
                  maxLength={6}
                  placeholder="••••••"
                  value={userForm.pin}
                  onChange={(e) => setUserForm({ ...userForm, pin: e.target.value.replace(/\D/g, "").slice(0, 6) })}
                />
              </label>
              <div className="user-access-note">
                <strong>Perfil: Inspetor</strong>
                <span>Esse acesso é indicado para mecânicos e outros colaboradores sem e-mail corporativo.</span>
              </div>
            </>
          ) : (
            <>
              <label>E-mail
                <input required type="email" value={userForm.email} onChange={(e) => setUserForm({ ...userForm, email: e.target.value })} />
              </label>
              <label>Senha temporária
                <input required minLength={8} type="password" value={userForm.password} onChange={(e) => setUserForm({ ...userForm, password: e.target.value })} />
              </label>
              <label>Perfil
                <select value={userForm.role} onChange={(e) => setUserForm({ ...userForm, role: e.target.value })}>
                  <option value="inspetor">Inspetor</option>
                  <option value="admin">Administrador</option>
                </select>
              </label>
            </>
          )}

          <button className="primary full">Criar usuário</button>
        </form>

        <div className="panel">
          <div className="panel-title-icon"><Users size={20} /><h2>Usuários cadastrados</h2></div>
          <div className="admin-users-list">
            {users.map((u) => (
              <article className="admin-user-row" key={u.id}>
                <div>
                  <b>{u.name}</b>
                  <small>
                    {u.login_username ? "Usuário: " + u.login_username + " · acesso por PIN" : u.email}
                  </small>
                  <span className={"user-role " + u.role}>{u.role === "admin" ? "Administrador" : "Inspetor"}</span>
                </div>
                <div className="actions">
                  <button className="secondary icon-action" onClick={() => openEditUser(u)}>
                    <Pencil size={15} /> Editar
                  </button>
                  <button className={u.active ? "danger-outline" : "secondary"} onClick={() => toggleUser(u)}>
                    {u.active ? "Desativar" : "Ativar"}
                  </button>
                </div>
              </article>
            ))}
          </div>
        </div>
      </div>

      {editingUser && (
        <div className="modal-backdrop" onClick={() => setEditingUser(null)}>
          <form className="modal admin-edit-modal" onSubmit={saveUser} onClick={(e) => e.stopPropagation()}>
            <h2>Editar usuário</h2>
            <label>Nome
              <input required value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} />
            </label>

            {editingUser.login_username ? (
              <>
                <label>Usuário de acesso
                  <input
                    required
                    minLength={3}
                    maxLength={30}
                    autoCapitalize="none"
                    value={editForm.login_username}
                    onChange={(e) => setEditForm({ ...editForm, login_username: e.target.value.toLowerCase() })}
                  />
                </label>
                <label>Novo PIN
                  <input
                    type="password"
                    inputMode="numeric"
                    pattern="[0-9]{6}"
                    minLength={6}
                    maxLength={6}
                    placeholder="Deixe em branco para manter o atual"
                    value={editForm.pin}
                    onChange={(e) => setEditForm({ ...editForm, pin: e.target.value.replace(/\D/g, "").slice(0, 6) })}
                  />
                </label>
                <div className="user-access-note">
                  <strong>Perfil: Inspetor</strong>
                  <span>Contas sem e-mail usam usuário + PIN e permanecem com perfil de Inspetor.</span>
                </div>
              </>
            ) : (
              <>
                <label>E-mail
                  <input required type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} />
                </label>
                <label>Perfil
                  <select value={editForm.role} onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}>
                    <option value="inspetor">Inspetor</option>
                    <option value="admin">Administrador</option>
                  </select>
                </label>
                <label>Nova senha
                  <input
                    type="password"
                    minLength={8}
                    placeholder="Deixe em branco para manter a atual"
                    value={editForm.password}
                    onChange={(e) => setEditForm({ ...editForm, password: e.target.value })}
                  />
                </label>
              </>
            )}

            <div className="modal-actions">
              <button type="button" className="secondary" onClick={() => setEditingUser(null)}>Cancelar</button>
              <button className="primary">Salvar alterações</button>
            </div>
          </form>
        </div>
      )}

      {editingItem && (
        <div className="modal-backdrop" onClick={() => setEditingItem(null)}>
          <form className="modal admin-edit-modal checklist-edit-modal" onSubmit={saveItem} onClick={(e) => e.stopPropagation()}>
            <div>
              <span className="section-kicker">CHECKLIST</span>
              <h2>Editar pergunta</h2>
            </div>

            <label>Seção
              <input
                required
                value={editItemForm.section}
                onChange={(e) => setEditItemForm({ ...editItemForm, section: e.target.value })}
              />
            </label>

            <label>Pergunta
              <textarea
                required
                value={editItemForm.question}
                onChange={(e) => setEditItemForm({ ...editItemForm, question: e.target.value })}
              />
            </label>

            <label>Classificação da gravidade
              <select
                value={editItemForm.severity}
                onChange={(e) => setEditItemForm({ ...editItemForm, severity: e.target.value })}
              >
                <option value="leve">Leve</option>
                <option value="moderada">Moderada</option>
                <option value="grave">Grave</option>
                <option value="gravissima">Gravíssima</option>
              </select>
            </label>

            <p className="muted checklist-edit-note">
              A alteração será aplicada às próximas inspeções. Inspeções já finalizadas continuam preservadas no histórico.
            </p>

            <div className="modal-actions">
              <button type="button" className="secondary" onClick={() => setEditingItem(null)}>Cancelar</button>
              <button className="primary">Salvar alterações</button>
            </div>
          </form>
        </div>
      )}

      <form className="panel form-grid" onSubmit={addItem}>
        <h2 className="full-span">Adicionar pergunta ao checklist</h2>
        <label>Modelo
          <select value={itemForm.template_id} onChange={(e) => setItemForm({ ...itemForm, template_id: e.target.value })}>
            {checklists.map((c) => <option key={c.template_id} value={c.template_id}>{c.type.name}</option>)}
          </select>
        </label>
        <label>Seção
          <input required value={itemForm.section} onChange={(e) => setItemForm({ ...itemForm, section: e.target.value })} />
        </label>
        <label className="full-span">Pergunta
          <input required value={itemForm.question} onChange={(e) => setItemForm({ ...itemForm, question: e.target.value })} />
        </label>
        <label>Classificação da gravidade
          <select value={itemForm.severity} onChange={(e) => setItemForm({ ...itemForm, severity: e.target.value })}>
            <option value="leve">Leve</option>
            <option value="moderada">Moderada</option>
            <option value="grave">Grave</option>
            <option value="gravissima">Gravíssima</option>
          </select>
        </label>
        <div className="form-actions"><button className="primary">Adicionar item</button></div>
      </form>

      {checklists.map((c) => (
        <div className="panel" key={c.template_id}>
          <h2>{c.type.name}</h2>
          {c.items.map((item) => (
            <article className="list-card compact" key={item.id}>
              <div>
                <b>{item.question}</b>
                <small>{item.section} · {severityLabel[item.severity] || item.severity} · classificação do administrador</small>
              </div>
              <div className="actions checklist-item-actions">
                <button className="secondary icon-action" onClick={() => openEditItem(item)}>
                  <Pencil size={15} /> Editar
                </button>
                <button className="secondary" onClick={() => toggleItem(item)}>
                  {item.active ? "Desativar" : "Ativar"}
                </button>
              </div>
            </article>
          ))}
        </div>
      ))}

      <div className="panel audit-panel">
        <div className="panel-title-icon"><ScrollText size={20} /><h2>Trilha de auditoria</h2></div>
        <p className="muted">Registro cronológico das principais ações administrativas e operacionais.</p>
        <div className="audit-list">
          {audit.map((entry) => (
            <article key={entry.id}>
              <div className="audit-dot" />
              <div>
                <strong>{entry.action.replaceAll("_", " ")}</strong>
                <span>{entry.user_name || "Sistema"} · {entry.entity}</span>
                <small>{formatBrt(entry.created_at)}</small>
              </div>
            </article>
          ))}
          {!audit.length && <div className="empty">Nenhum evento de auditoria registrado.</div>}
        </div>
      </div>
    </section>
  );
}
